import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders,
         HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ConfigParserError } from 'core/config/config-parser';
import { GalleryConstants } from 'core/constants';
import { TimeoutError } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { Utils } from '../utils';
import { StageUtils } from '../utils.stage';

const UPLOAD_TIMEOUT = 15 * 60 * 1000; // 15min
const HEAD_TIMEOUT = 60; // in seconds

export interface ImageStoreRequestError
{
  status: number;
  statusText: string;
  detail?: string;
}

export interface ImageStoreBaseInformation
{
  location: string;
  width: number;
  height: number;
  contentType: string;
  expirationDate?: Date;
  contentCenter?: string;
}

export interface ImageStorePostInformation extends ImageStoreBaseInformation
{
  id: string;
  blob?: Blob;
}

@Injectable()
export class ImageStoreService
{
  private static readonly DEFAULT_FILTER: string =
      '?limit=%7B%22long_side%22%3A%2011000%7D&center-of-content=all';
  private _galleriesImageStoreDomain: string;
  private _galleriesImageStoreURL: string;
  private _galleryImageDisposition: string;

  constructor(private http: HttpClient)
  {}

  /**
   * @throws ImageStoreRequestError
   */
  public postImage(imageFile: Blob): Promise<ImageStorePostInformation>
  {
    return this.postImageOrLocation(
      new HttpRequest(
          'POST',
          this._galleriesImageStoreURL + ImageStoreService.DEFAULT_FILTER,
          imageFile, {
            headers: new HttpHeaders({
              'image-disposition': this.galleryImageDisposition
            })
          }));
  }

  /**
   * @throws ImageStoreRequestError
   */
  public postSvg(svg: string): Promise<ImageStorePostInformation>
  {
    return this.postImageOrLocation(
      new HttpRequest(
          'POST',
          this._galleriesImageStoreURL,
          svg, {
            headers: new HttpHeaders({
              'content-type': 'image/svg',
              'image-disposition': this.galleryImageDisposition
            })
          }));
  }

  public async getImageInfo(url: string): Promise<ImageStoreBaseInformation>
  {
    const queryStart = url.indexOf('?');
    const base = queryStart === -1 ? url : url.slice(0, queryStart);
    const query = queryStart === -1 ? '' : url.slice(queryStart);
    const finalUrl = base.replace(/\/+$/, '') + '/meta' + query;

    try {
      const req = new HttpRequest('HEAD', finalUrl);
      const resp = await this.doImageStoreHeadRequest(req);

      if (! (resp instanceof HttpResponse) || resp.status !== 204) {
        throw resp;
      }

      return {
        location: url,
        width: parseInt(resp.headers.get('Width'), 10),
        height: parseInt(resp.headers.get('Height'), 10),
        contentType: resp.headers.get('Content-Type'),
        expirationDate: resp.headers.has('expires') ?
            new Date(resp.headers.get('expires')) : undefined,
        contentCenter: undefined,
      };
    }
    catch (error) {
      throw this.buildImageStoreRequestError(error);
    }
  }

  public async renderSvg(svg, dimensions): Promise<HTMLImageElement>
  {
    const filters = [
      ['stretch', {
        width: Math.round(dimensions.width),
        height: Math.round(dimensions.height)
      }],
      ['convert', 'image/png'],
    ];

    const filterConfig = filters
      .map(f => f[0] + '=' + encodeURIComponent(JSON.stringify(f[1])))
      .join('&');

    const serializedSvg = StageUtils.withoutNonBreakingSpaces(
        new XMLSerializer().serializeToString(svg));

    const req = new HttpRequest(
      'POST',
      this._galleriesImageStoreURL + '?' + filterConfig,
      StageUtils.sanitizeStructure(serializedSvg),
      {
        headers: new HttpHeaders({
          'content-type': 'image/svg',
          'accept': 'image/png',
          'persist': 'false',
        }),
        responseType: 'blob',
      });

    const info = await this.postImageOrLocation(req);

    const img = new Image();
    const result = new Promise<HTMLImageElement>(
      (res) => {
        img.onload = () => res(img);
      });

    img.src = window.URL.createObjectURL(info.blob);
    return result;
  }

  private async doImageStorePostRequest(req: HttpRequest<any>): Promise<any>
  {
    return new Promise((resolve, reject) => {
      this.http.request(req).pipe(
        timeout(UPLOAD_TIMEOUT))
        .pipe(Utils.retryPipe())
        .subscribe((ev: HttpEvent<any>) => {
          if (ev.type === HttpEventType.Response) {
            resolve(ev);
          }
        }, reject);
    });
  }

  private async doImageStoreHeadRequest(req: HttpRequest<any>): Promise<any>
  {
    return new Promise((resolve, reject) => {
      this.http.request(req).pipe(
        timeout(HEAD_TIMEOUT * 1000))
        .pipe(Utils.retryPipe())
        .subscribe((ev: HttpEvent<any>) => {
          if (ev.type === HttpEventType.Response) {
            resolve(ev);
          }
        }, reject);
    });
  }

  /**
   * @throws ImageStoreRequestError
   */
  private async postImageOrLocation(req: HttpRequest<any>):
      Promise<ImageStorePostInformation>
  {
    const absoluteLocation = (url: string | undefined) => {
      if (!url) {
        return undefined;
      }

      return Utils.hasHttpSchema(url)
        ? url
        : (Utils.isRelativeToDomain(url)
           ? this._galleriesImageStoreDomain + url
           : undefined);
    };

    try {
      const resp = await this.doImageStorePostRequest(req);

      if (! (resp instanceof HttpResponse) || resp.status !== 201) {
        throw resp;
      }

      const url = absoluteLocation(resp.headers.get('Location'));

      const blob = req.responseType === 'blob'
          ? (resp.body as Blob)
          : undefined;

      if (! (url || blob)) {
        throw resp;
      }

      return {
        id: resp.body['id'] as string,
        contentType: resp.body['content_type'] as string,
        location: url,
        blob: blob,
        width: parseInt(resp.headers.get('Width'), 10),
        height: parseInt(resp.headers.get('Height'), 10),
        expirationDate: resp.headers.has('expires') ?
            new Date(resp.headers.get('expires')) : undefined,
        contentCenter: resp.headers.has('center-of-content') ?
            resp.headers.get('center-of-content') : undefined,
      };
    }
    catch (error) {
      throw this.buildImageStoreRequestError(error);
    }
  }

  public get galleriesImageStoreDomain(): string
  {
    return this._galleriesImageStoreDomain;
  }

  public set galleriesImageStoreDomain(domain: string)
  {
    this._galleriesImageStoreDomain = domain;
    this._galleriesImageStoreURL = domain + '/images/';
  }

  public get galleriesImageStoreURL(): string
  {
    return this._galleriesImageStoreURL;
  }

  public set galleriesImageStoreURL(url: string)
  {
    this._galleriesImageStoreURL = url;
  }

  public get galleryImageDisposition(): string
  {
    return this._galleryImageDisposition
      || GalleryConstants.GALLERY_IMAGE_DISPOSITION;
  }

  public set galleryImageDisposition(disposition: string)
  {
    this._galleryImageDisposition = disposition;
  }

  private buildImageStoreRequestError(error, url?: string):
      ImageStoreRequestError
  {
    if (!url) {
      url = this._galleriesImageStoreURL;
    }
    let _error: ImageStoreRequestError;
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
          _error = <ImageStoreRequestError> {
              status: 404,
              statusText: 'Not Found',
              detail: url
          };
      }
      else {
        _error = <ImageStoreRequestError> {
            status: error.status,
            statusText: error.statusText,
            detail: error.error['detail']
        };
      }
    }
    else if (error instanceof TimeoutError) {
        _error = <ImageStoreRequestError> {
          status: 499,
          statusText: 'Client Closed Request (TimeoutError)',
          detail: error.stack
        };
    }
    else if (error instanceof ConfigParserError) {
      _error = <ImageStoreRequestError> {
          status: <any>error.name,
          statusText: error.reference.join(' '),
          detail: error.message
      };
    }
    else {
      _error = <ImageStoreRequestError> {
          status: error.status,
          statusText: error.statusText,
          detail: 'undefined Reason'
      };
    }
    return _error;
  }
}
